import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import { View } from 'react-native';

import { BodyText, Button, IconLink, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Warning message related to rewards.
 */
export const BagRewardWarning = (props: BagRewardWarningProps) => {
  const { message, isRewardMissingSku, onClose, onViewMenu } = props;

  const { formatMessage } = useIntl();

  const cta = isRewardMissingSku
    ? formatMessage(messages.viewMenuCta)
    : formatMessage(messages.cta);

  const accessibilityLabel = isRewardMissingSku
    ? formatMessage(messages.accessibilityLabelViewMenu)
    : formatMessage(messages.accessibilityLabel);

  return (
    <View style={styles.container}>
      <IconLink
        name="IconClose"
        palette="muted"
        style={styles.closeCta}
        accessibilityLabel={formatMessage(messages.closeAccessibilityLabel)}
        onPress={onClose}
      />

      <BodyText sizeMatch={['24']}>{message}</BodyText>

      <Button
        size="large-wide"
        palette="primaryOutline"
        accessibilityLabel={accessibilityLabel}
        onPress={isRewardMissingSku ? onViewMenu : onClose}
      >
        <BodyText style={styles.ctaColor} sizeMatch={['18']}>
          {cta}
        </BodyText>
      </Button>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  fallback: {
    defaultMessage: 'Something went wrong, please try again later.',
    description: 'Bag | Reward warning | Fallback message',
  },
  cta: {
    defaultMessage: 'Got it',
    description: 'Bag | Reward warning | CTA',
  },
  viewMenuCta: {
    defaultMessage: 'View menu',
    description: 'Bag | Reward warning | View Menu CTA',
  },
  accessibilityLabel: {
    defaultMessage: 'Confirm reward warning',
    description: 'Bag | Reward warning | a11y',
  },
  accessibilityLabelViewMenu: {
    defaultMessage: 'Confirm reward warning and view menu',
    description: 'Bag | Reward warning | a11y',
  },
  closeAccessibilityLabel: {
    defaultMessage: 'Close',
    description: 'Bag | Reward warning | Close a11y',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingTop: theme.spacing['4'],
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['10'],
    gap: theme.spacing['6'],
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeCta: {
    width: 40,
    height: 40,
    alignSelf: 'flex-end',
  },
  ctaColor: {
    color: theme.colors.KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type BagRewardWarningProps = {
  message?: string;
  isRewardMissingSku?: boolean;
  onClose: () => void;
  onViewMenu: () => void;
};
